import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import DashboardLayout from '../Layout/DashboardLayout'
import axios from 'axios'
import { BASE_URL, NAME } from '../apiConfig'
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { useTranslation } from '../LanguageContext'
import { useAuth } from '../AuthContext'
import Loading from '../element/Loading'
import nextlead_logo from '../imgs/nextlead-logo.png'
import topseller_logo from '../imgs/topseller-logo.png'
import { Printer, ArrowLeft, Wallet, HandCoins } from 'lucide-react'

const printStyles = `
  @page { margin: 0; size: auto; }
  @media print {
    body * { visibility: hidden; }
    .print-area, .print-area * { visibility: visible; }
    .print-area { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 20px; padding-right: 60px; padding-left: 60px; border: none !important; box-shadow: none !important; }
    .print-hidden { display: none !important; }
  }
`

export default function InvoiceDetail() {
  const { id } = useParams()
  const { t } = useTranslation()
  const { can } = useAuth()
  const navigate = useNavigate()
  const token = Cookies.get('WEB_SSR')
  const printRef = useRef()
  const logos = {
    nextlead: nextlead_logo,
    topseller: topseller_logo,
  };
  const logo = logos[NAME];

  const [invoice, setInvoice] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = printStyles
    document.head.appendChild(style)
    if (!token) {
      navigate('/')
      return
    }
    fetchInvoice()
    return () => style.remove()
  }, [id])

  const fetchInvoice = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${BASE_URL}/api/invoices/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
      if (response.data.success) {
        setInvoice(response.data.data)
      }
    } catch (error) {
      toast.error(t('invoices.error.loading_products'))
      navigate('/invoices')
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  const formatDate = (date) => {
    if (!date) return '—'
    return new Date(date).toLocaleDateString()
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center min-h-[85vh]">
          <Loading size={12} />
        </div>
      </DashboardLayout>
    )
  }

  if (!invoice) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <p className="text-gray-500 dark:text-gray-400">{t('invoices.no_invoices')}</p>
        </div>
      </DashboardLayout>
    )
  }

  const paid = (invoice.payments || []).reduce((sum, p) => sum + Number(p.amount), 0)
  const balance = Math.max(invoice.total - paid, 0)

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
        <div className="flex items-center justify-between print-hidden">
          {can('invoices.view') && (
            <button
              onClick={() => navigate('/invoices')}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('invoices.all_invoices')}
            </button>
          )}
          {can('invoices.view') && (
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              <Printer className="h-4 w-4" />
              {t('invoices.print')}
            </button>
          )}
        </div>

        <div ref={printRef} className="print-area rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="flex flex-col gap-3 py-5 sm:flex-row sm:justify-between">
            <div>
              <div className="mt-4">
                <img
                  src={logo}
                  alt="Logo"
                  className={`${NAME === 'nextlead' ? 'h-12' : 'h-24'} object-contain`}
                />
              </div>
              
            </div>
            <div className="space-y-1 text-right text-sm text-gray-700 dark:text-gray-300">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">
                 N°: {invoice.invoice_code}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {new Date(invoice.created_at).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false,
                            })}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('invoices.client_name')}</p>
              <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-50">
                {invoice.client_name}
              </p>
            </div>
            {invoice.client_phone && (
              <div>
                <p className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('invoices.client_phone')}</p>
                <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-50">{invoice.client_phone}</p>
              </div>
            )}
          </div>

          <div className="mt-6">
            <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-50">{t('invoices.items_count')}</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('invoices.product')}</th>
                    <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('invoices.variant')}</th>
                    <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('invoices.quantity')}</th>
                    <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('invoices.unit_price')}</th>
                    <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('invoices.line_total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.items || []).map((item, idx) => (
                    <tr key={item.id ?? idx} className="border-b border-gray-200 dark:border-gray-700">
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{item.product_name}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{item.variant_reference || '—'}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{item.quantity}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{Number(item.unit_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{Number(item.line_total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {(invoice.remise > 0 || invoice.items) && (
                    <tr className="border-t border-gray-200 dark:border-gray-700">
                      <td colSpan="4" className="px-4 py-2 text-right text-sm text-gray-600 dark:text-gray-400">{t('invoices.subtotal')}</td>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{Number(Number(invoice.total || 0) + Number(invoice.remise || 0)).toFixed(2)}</td>
                    </tr>
                  )}
                  {invoice.remise > 0 && (
                    <tr className="border-t border-gray-200 dark:border-gray-700">
                      <td colSpan="4" className="px-4 py-2 text-right text-sm text-red-600 dark:text-red-400">{t('invoices.remise')}</td>
                      <td className="px-4 py-2 text-sm text-red-600 dark:text-red-400">-{Number(invoice.remise).toFixed(2)}</td>
                    </tr>
                  )}
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600">
                    <td colSpan="4" className="px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-50">{t('invoices.total')}</td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-gray-50">{Number(invoice.total).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {(invoice.payments || []).length > 0 && (
            <div className="mt-6">
              <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold text-gray-900 dark:text-gray-50">
                <Wallet className="h-4 w-4" />
                {t('invoices.payment_history')}
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('invoices.client_history.payment_amount')}</th>
                      <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('invoices.client_history.payment_date')}</th>
                      <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('invoices.client_history.payment_note')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((payment) => (
                      <tr key={payment.id} className="border-b border-gray-200 dark:border-gray-700">
                        <td className="px-4 py-3 font-medium text-green-600 dark:text-green-400">{Number(payment.amount).toFixed(2)}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{formatDate(payment.payment_date)}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{payment.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                  <HandCoins className="h-4 w-4" />
                  <span className="font-medium">{t('invoices.price_paye')}:</span> {paid.toFixed(2)}
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  <Wallet className="h-4 w-4" />
                  <span className="font-medium">{t('invoices.balance')}:</span> {balance.toFixed(2)}
                </div>
              </div>
            </div>
          )}

          {invoice.client_history && (
            <div className="mt-6 rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50">
              <h3 className="mb-3 text-xs font-semibold text-gray-900 dark:text-gray-50">{t('invoices.client_history.title')}</h3>
              <div className="flex flex-wrap gap-4 text-[11px]">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t('invoices.client_history.total_invoiced')}:</span>{' '}
                  <span className="font-medium text-gray-900 dark:text-gray-50">{Number(invoice.client_history.total_invoiced).toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t('invoices.client_history.total_paid')}:</span>{' '}
                  <span className="font-medium text-green-600 dark:text-green-400">{Number(invoice.client_history.total_paid).toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t('invoices.client_history.balance')}:</span>{' '}
                  <span className="font-medium text-red-600 dark:text-red-400">{Number(invoice.client_history.balance).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
